import { launchContentPack } from "@stormglass/game-content";
import { localizer } from "@stormglass/game-localization";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Battlefield } from "../components/Battlefield";
import { GameHud } from "../components/GameHud";
import { GameOverlays } from "../components/GameOverlays";
import { UpgradeDock } from "../components/UpgradeDock";
import { useGameHost, useGameState } from "../game/GameContext";

const tutorialSeen = (id: string): boolean =>
  localStorage.getItem(`stormglass-hint:${id}`) === "1";

export function GameScreen(): React.JSX.Element {
  const host = useGameHost();
  const { ui, snapshot } = useGameState();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<string[]>([]);
  useEffect(() => {
    if (ui.phase === "idle") host.startFreshRun();
  }, [host, ui.phase]);
  useEffect(() => {
    if (ui.phase === "ended")
      void navigate("/results/latest", { replace: true });
  }, [navigate, ui.phase]);
  useEffect(() => {
    const keyboard = (event: KeyboardEvent): void => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement
      )
        return;
      if (event.key.toLowerCase() === "p" || event.key === " ") {
        event.preventDefault();
        host.command({ type: ui.paused ? "resume-run" : "pause-run" });
      }
      if (event.key === "1") host.command({ type: "set-game-speed", speed: 1 });
      if (event.key === "2") host.command({ type: "set-game-speed", speed: 2 });
      if (event.key === "Escape" && !ui.paused)
        host.command({ type: "pause-run" });
    };
    window.addEventListener("keydown", keyboard);
    return () => {
      window.removeEventListener("keydown", keyboard);
    };
  }, [host, ui.paused]);
  const affordable = useMemo(
    () =>
      Object.values(launchContentPack.upgrades).some((upgrade) => {
        if (upgrade.category !== "basic") return false;
        const level = snapshot.runUpgradeLevels[upgrade.id] ?? 0;
        return (
          level < upgrade.maxLevel &&
          ui.charge >=
            Math.ceil(upgrade.baseCost * Math.pow(upgrade.costGrowth, level))
        );
      }),
    [snapshot.runUpgradeLevels, ui.charge],
  );
  const hint =
    !tutorialSeen("first-enemy") &&
    snapshot.enemies.length > 0 &&
    !dismissed.includes("first-enemy")
      ? { id: "first-enemy", text: localizer.t("game.firstEnemy") }
      : !tutorialSeen("first-upgrade") &&
          affordable &&
          !dismissed.includes("first-upgrade")
        ? { id: "first-upgrade", text: localizer.t("game.firstUpgrade") }
        : null;
  const dismissHint = (): void => {
    if (hint === null) return;
    localStorage.setItem(`stormglass-hint:${hint.id}`, "1");
    setDismissed((current) => [...current, hint.id]);
  };
  return (
    <div className="game-screen">
      <GameHud />
      <div className="game-screen__arena">
        <Battlefield />
        {hint !== null && (
          <aside className="coachmark" role="status">
            <span className="coachmark__glyph">⌁</span>
            <p>{hint.text}</p>
            <button type="button" onClick={dismissHint}>
              {localizer.t("action.dismiss")}
            </button>
          </aside>
        )}
      </div>
      <UpgradeDock />
      <GameOverlays />
    </div>
  );
}
